'use client';

import { Suspense, useEffect, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { LoginForm } from '@/components/login-form';
import { useAuth } from '@/contexts/auth-context';
import { toast } from 'sonner';

// Disable static generation for this page
export const dynamic = 'force-dynamic';

const BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH || '';

// SSO authorize URL is constructed entirely server-side by /api/auth/oauth-start.
// No NEXT_PUBLIC_SSO_* env vars needed — enables single-image promotion across envs.
async function buildSSOLoginUrl(redirectPath?: string): Promise<{ url: string; useSso: boolean }> {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : undefined;
  const resp = await fetch(`${BASE_PATH}/api/auth/oauth-start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ redirectPath, hostname }),
  });
  if (!resp.ok) throw new Error(`oauth-start returned ${resp.status}`);
  const data = await resp.json();
  return { url: data.authorize_url, useSso: data.use_sso };
}

// USE_SSO default — overridden by server response on first fetch.
// Kept as a client-side default so the loading state renders correctly
// before the first oauth-start round-trip completes.
const USE_SSO = process.env.NEXT_PUBLIC_USE_SSO !== 'false';

// Inner component that uses useSearchParams
function LoginPageContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { isAuthenticated, isAuthLoading, login } = useAuth();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const tPage = useTranslations('pages.login');
  const tCommon = useTranslations('common');

  // Preserve same-origin OAuth/MCP authorization requests through the SSO
  // round-trip. The proxy supplies this query parameter when an unauthenticated
  // client reaches /oauth/authorize.
  useEffect(() => {
    const redirect = searchParams.get('redirect');
    if (
      redirect &&
      redirect.startsWith('/') &&
      !redirect.startsWith('//') &&
      !/[\r\n<>"']/.test(redirect)
    ) {
      sessionStorage.setItem('redirectAfterLogin', redirect);
    }
  }, [searchParams]);

  // Handle error from OAuth callback
  useEffect(() => {
    const error = searchParams.get('error');
    if (error) {
      toast.error(decodeURIComponent(error));
    }
  }, [searchParams]);

  useEffect(() => {
    // Redirect to intended destination if already authenticated
    if (isAuthenticated) {
      const isEmulator = typeof window !== 'undefined' && window.location.hostname.startsWith('emulator.');
      const defaultPath = isEmulator ? '/emulator' : '/agents';
      const redirectPath = sessionStorage.getItem('redirectAfterLogin') || defaultPath;
      sessionStorage.removeItem('redirectAfterLogin');
      router.push(redirectPath);
    }
  }, [isAuthenticated, router]);

  // Auto-redirect to SSO if enabled and not already authenticated
  useEffect(() => {
    if (USE_SSO && !isAuthenticated && !isAuthLoading && !isRedirecting) {
      const error = searchParams.get('error');
      // Don't auto-redirect if there was an error (let user see the error and retry)
      if (!error) {
        setIsRedirecting(true);
        const isEmulator = typeof window !== 'undefined' && window.location.hostname.startsWith('emulator.');
        const defaultPath = isEmulator ? '/emulator' : '/agents';
        const redirectPath = sessionStorage.getItem('redirectAfterLogin') || defaultPath;
        void buildSSOLoginUrl(redirectPath).then(({ url, useSso }) => {
          if (useSso) window.location.href = url;
          else setIsRedirecting(false);
        }).catch(() => setIsRedirecting(false));
      }
    }
  }, [isAuthenticated, isAuthLoading, isRedirecting, searchParams]);

  const handleSSOLogin = () => {
    setIsRedirecting(true);
    const isEmulator = typeof window !== 'undefined' && window.location.hostname.startsWith('emulator.');
    const defaultPath = isEmulator ? '/emulator' : '/agents';
    const redirectPath = sessionStorage.getItem('redirectAfterLogin') || defaultPath;
    void buildSSOLoginUrl(redirectPath).then(({ url }) => {
      window.location.href = url;
    }).catch(() => setIsRedirecting(false));
  };

  const handleLogin = async (username: string, password: string) => {
    try {
      await login(username, password);
      // Login successful (SIP registration is handled in login function)
      // Navigate to intended destination or default to pipelines page
      const isEmulator = typeof window !== 'undefined' && window.location.hostname.startsWith('emulator.');
      const defaultPath = isEmulator ? '/emulator' : '/agents';
      const redirectPath = sessionStorage.getItem('redirectAfterLogin') || defaultPath;
      sessionStorage.removeItem('redirectAfterLogin');
      router.push(redirectPath);
    } catch (error: unknown) {
      // Error is already shown via toast in login function
      // Don't navigate on error
      console.error('Login failed:', error);
    }
  };

  // Show loading if already authenticated (will redirect) or if redirecting to SSO
  if (isAuthenticated || (USE_SSO && isRedirecting)) {
    return (
      <div className="flex min-h-svh w-full items-center justify-center">
        <div className="text-muted-foreground">
          {isAuthenticated ? tPage('redirecting') : tPage('redirectingToSSO')}
        </div>
      </div>
    );
  }

  // Show loading during auth check
  if (isAuthLoading) {
    return (
      <div className="flex min-h-svh w-full items-center justify-center">
        <div className="text-muted-foreground">{tCommon('actions.loading')}</div>
      </div>
    );
  }

  return (
    <div className="flex min-h-svh w-full items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-sm">
        <LoginForm
          onLoginSuccess={handleLogin}
          onSSOLogin={handleSSOLogin}
          useSSO={USE_SSO}
        />
      </div>
    </div>
  );
}

// Main component with Suspense boundary
export default function LoginPage() {
  const tCommon = useTranslations('common');
  return (
    <Suspense fallback={
      <div className="flex min-h-svh w-full items-center justify-center">
        <div className="text-muted-foreground">{tCommon('actions.loading')}</div>
      </div>
    }>
      <LoginPageContent />
    </Suspense>
  );
}
